import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {
  DocumentService,
  DocumentSummary,
  PartyType,
  TodoPriorityId,
  TodoPrioritySelect,
  TodoStatusSummary,
  UserSelect
} from '../../../../../api';
import {TranslatePipe} from '@ngx-translate/core';
import {ElciaDatePicker} from '../../../../components/date-picker/date-picker';
import {PartyDropdown} from '../../../../components/party-dropdown/party-dropdown';
import {TodoPriorityDropdown} from '../todo-priority-dropdown/todo-priority-dropdown';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {TextInput} from '../../../../components/text-input/text-input';
import {TextArea} from '../../../../components/text-area/text-area';
import {TodoStatusDropdown} from '../todo-status-dropdown/todo-status-dropdown';
import {UserDropdown} from '../../../../components/user-dropdown/user-dropdown';
import {Divider} from 'primeng/divider';
import {Label} from '../../../../components/label/label';
import {SubTodoForm} from '../sub-todo-form/sub-todo-form';
import {TodoFormModel} from '../../models/todo-form-model';
import {ChildTodoModelForm} from '../../models/child-todo-model-form';
import {TodoFormValue} from '../../models/todo-form-value';
import {ChildTodoValue} from '../../models/child-todo-value';
import {ToggleButtonModule} from 'primeng/togglebutton';
import {ToggleTodoPrivate} from '../toggle-todo-private/toggle-todo-private';
import {MentionableTodo, TodoComment} from '../todo-comment/todo-comment';
import {DocumentUploader} from '../../../../components/document-uploader/document-uploader';
import {NumericInput} from '../../../../components/numeric-input/numeric-input';
import {ElciaBadge} from '../../../../components/badge/badge';
import {CheckBox} from '../../../../components/check-box/check-box';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {TodoTimesheetList} from '../todo-timesheet-list/todo-timesheet-list';


@Component({
  selector: 'elcia-todo-form',
  imports: [
    TranslatePipe,
    ToggleButtonModule,
    ElciaDatePicker,
    PartyDropdown,
    TodoPriorityDropdown,
    TextInput,
    TextArea,
    TodoStatusDropdown,
    UserDropdown,
    Divider,
    Label,
    SubTodoForm,
    ToggleTodoPrivate,
    TodoComment,
    DocumentUploader,
    NumericInput,
    DatePipe,
    CheckBox,
    ElciaBadge,
    ElciaButton,
    TodoTimesheetList,
  ],
  templateUrl: './todo-form.html',
  styleUrl: './todo-form.scss',
})
export class TodoForm {
  private readonly documentService = inject(DocumentService);

  partyTypes = signal<PartyType[]>([PartyType.Company, PartyType.Contact]);
  initialValue = input<TodoFormValue>();
  // Only the author can set a todo as private (defaults to true so creation shows the toggle).
  isAuthor = input<boolean>(true);
  // Read-only, so it stays out of TodoFormValue which carries the editable fields only.
  completedAt = input<Date | null>(null);
  documents = input<DocumentSummary[]>([]);
  statuses = input<TodoStatusSummary[]>([]);
  priorities = input<TodoPrioritySelect[]>([]);
  users = input<UserSelect[]>([]);
  storeTodos = input<MentionableTodo[]>([]);
  onSubmit = output<TodoFormValue>();

  pendingFiles = signal<File[]>([]);
  pendingDeletionIds = signal<number[]>([]);

  protected rightPanel = signal<'chat' | 'timesheets'>('chat');

  protected linkedTodoIds = computed<number[]>(() => {
    const value = this.initialValue();
    if (!value?.id) return [];
    const childIds = value.children?.map(child => child.id).filter(id => id !== null) ?? [];
    return [value.id, ...childIds];
  });

  protected displayedDocuments = computed(() =>
    this.documents().filter(d => !this.pendingDeletionIds().includes(d.id))
  );

  protected todoForm = new FormGroup<TodoFormModel>({
    title: new FormControl('', Validators.required),
    description: new FormControl(null),
    dueDate: new FormControl(null),
    concernId: new FormControl(null),
    priorityName: new FormControl(TodoPriorityId.UNDEFINED),
    children: new FormArray<FormGroup<ChildTodoModelForm>>([]),
    isPrivate: new FormControl(false),
    isDone: new FormControl(false),
    statusId: new FormControl(null, Validators.required),
    estimatedTimeInDays: new FormControl(null),
    assignedToIds: new FormControl([]),
  })

  private formValues = toSignal(this.todoForm.valueChanges.pipe(debounceTime(300)), {initialValue: this.todoForm.value});

  localTodos = computed<MentionableTodo[]>(() => {
    const val = this.formValues();
    const result: MentionableTodo[] = [];
    if (val.title) {
      result.push({id: this.initialValue()?.id ?? null, title: val.title});
    }
    val.children?.forEach(child => {
      if (child.title) result.push({id: child.id ?? null, title: child.title});
    });
    return result;
  });

  onInitialValueChange = effect(() => {
    const val = this.initialValue();
    this.pendingFiles.set([]);
    this.pendingDeletionIds.set([]);
    this.todoForm.controls.children.clear();
    if (val) {
      this.todoForm.patchValue({
        title: val.title,
        description: val.description,
        dueDate: val.dueDate,
        concernId: val.concernId,
        priorityName: val.priorityName,
        statusId: val.statusId,
        estimatedTimeInDays: val.estimatedTimeInDays,
        isPrivate: val.isPrivate,
        isDone: val.isDone,
        assignedToIds: val.assignedToIds,
      });
      val.children?.forEach(child => {
        this.todoForm.controls.children.push(this.createChildFormGroup(child));
      });
    }
    this.ensureTrailingRow();
  });

  private setDefaultStatus = effect(() => {
    const statuses = this.statuses();
    if (!this.initialValue() && !this.todoForm.controls.statusId.value && statuses.length > 0) {
      const defaultStatus = statuses.find(s => s.isDefault);
      if (defaultStatus?.id) {
        this.todoForm.controls.statusId.setValue(defaultStatus.id);
      }
    }
  });

  constructor() {
    // Mirrors the domain rule: completing the parent completes its children, while reopening it leaves them untouched.
    this.todoForm.controls.isDone.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(isDone => {
        if (!isDone) return;
        this.todoForm.controls.children.controls.forEach(child => child.controls.isDone.setValue(true));
      });
  }

  protected addPendingFiles(files: File[]): void {
    this.pendingFiles.update(current => [...current, ...files]);
  }

  protected removePendingFile(index: number): void {
    this.pendingFiles.update(current => current.filter((_, i) => i !== index));
  }

  protected markForDeletion(doc: DocumentSummary): void {
    this.pendingDeletionIds.update(ids => [...ids, doc.id]);
  }

  protected downloadDocument(doc: DocumentSummary): void {
    this.documentService.downloadDocument(doc.id).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  resetPendingDocuments(): void {
    this.pendingFiles.set([]);
    this.pendingDeletionIds.set([]);
  }

  public submit(): void {
    if (this.todoForm.invalid) {
      this.todoForm.markAllAsTouched();
      return;
    }
    const value = this.todoForm.value as TodoFormValue;
    value.children = value.children?.filter(c => c.title?.trim()) ?? [];
    this.onSubmit.emit(value);
  }

  protected removeChild(index: number): void {
    this.todoForm.controls.children.removeAt(index);
  }

  protected ensureTrailingRow(): void {
    const children = this.todoForm.controls.children;
    const last = children.at(children.length - 1);
    if (!last || last.controls.title.value?.trim()) {
      children.push(this.createChildFormGroup());
    }
  }

  private createChildFormGroup(child?: ChildTodoValue): FormGroup<ChildTodoModelForm> {
    return new FormGroup<ChildTodoModelForm>({
      id: new FormControl(child?.id ?? null),
      title: new FormControl(child?.title ?? null),
      dueDate: new FormControl(child?.dueDate ?? null),
      assignedToId: new FormControl(child?.assignedToId ?? null),
      isDone: new FormControl(child?.isDone ?? false),
    })
  }
}


